import { motion } from "framer-motion";
import { Building2, Landmark, Store, Stethoscope, Check, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDemo } from "../context/DemoContext";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const meta = [
  { icon: Building2, color: "#F05A28", image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwY29sbGFib3JhdGlvbiUyMG9mZmljZXxlbnwwfHx8fDE3ODQ4NDcyODZ8MA&ixlib=rb-4.1.0&q=85" },
  { icon: Landmark, color: "#4BB5E7", image: "https://images.unsplash.com/photo-1651514645933-c26e0eb4ace3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxtZW1iZXJzaGlwJTIwY29tbXVuaXR5JTIwbmV0d29ya2luZyUyMGV2ZW50fGVufDB8fHx8MTc4NDg0NzI4Nnww&ixlib=rb-4.1.0&q=85" },
  { icon: Store, color: "#F7931D", image: "https://images.pexels.com/photos/7693692/pexels-photo-7693692.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { icon: Stethoscope, color: "#3AAA35", image: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwc2NpZW50aWZpYyUyMGNvbmZlcmVuY2UlMjBwcm9mZXNzaW9uYWxzfGVufDB8fHx8MTc4NDg0NzI4Nnww&ixlib=rb-4.1.0&q=85" },
];

export default function WhoWeServe() {
  const { openDemo } = useDemo();
  const { t } = useTranslation();
  const segments = t("whoPage.segments", { returnObjects: true });
  return (
    <div data-testid="who-we-serve-page" className="pt-32 md:pt-40">
      <Seo {...PAGE_SEO["/who-we-serve"]} path="/who-we-serve" />
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-cyan mb-4">{t("whoPage.eyebrow")}</p>
        <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tight leading-[0.98] max-w-4xl">{t("whoPage.titleBefore")}<span className="joyn-gradient-text">{t("whoPage.titleBrand")}</span></h1>
        <p className="mt-5 text-lg text-muted-foreground max-w-2xl">{t("whoPage.desc")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24 space-y-20">
        {segments.map((s, i) => {
          const Icon = meta[i].icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="[direction:ltr] rounded-3xl overflow-hidden border border-border shadow-sm"><img src={meta[i].image} alt={s.t} className="w-full h-72 md:h-96 object-cover" /></div>
              <div className="[direction:ltr]">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white mb-5" style={{ background: meta[i].color }}><Icon size={24} /></span>
                <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-3">{s.t}</h2>
                <p className="text-lg text-muted-foreground mb-6">{s.b}</p>
                <ul className="space-y-3">
                  {s.p.map((p) => (
                    <li key={p} className="flex items-start gap-3"><span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-lg text-white shrink-0" style={{ background: meta[i].color }}><Check size={14} /></span><span className="leading-snug">{p}</span></li>
                  ))}
                </ul>
              </div>
            </motion.div>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="rounded-3xl bg-foreground text-white p-10 md:p-16 text-center relative overflow-hidden">
          <div className="h-1.5 joyn-gradient-bar absolute top-0 inset-x-0" />
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-4">{t("whoPage.ctaTitle")}</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">{t("whoPage.ctaDesc")}</p>
          <button data-testid="wws-demo-btn" onClick={openDemo} className="inline-flex items-center gap-2 bg-white text-foreground font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("nav.demo")} <ArrowRight size={18} /></button>
        </div>
      </section>
    </div>
  );
}
